import './CombinedScheduler.scss'
import { SchedulerModal } from '../../common/Modals/SchedulerModal/SchedulerModal'
import { ReturnSchedulerModal, ReturnsDataRow } from '../Returns/ReturnSchedulerModal'
import { GridRow as DeliveryGridRow } from '../Delivery'
import { Modal } from '../../common/Modals/Modal'
import { useTranslation } from 'react-i18next'
import { useState } from 'react'
import { VehicleOrderInputModel, VehicleReturnInputModel } from '@ail/orders-api'
import { ContractViewModel } from '@ail/contracts-api'
import { usePutVehicleOrderMutation, usePutVehicleReturnMutation } from '../../../redux/api/ordersApi'
import { zeroTimeZoneDate } from '../../../helpers/Utils'
interface Props {
  dataRow: DeliveryGridRow
  visible: boolean
  onClose: () => void
  getCostCenterName: (contract: ContractViewModel) => string
}

export interface DeliveryFormData extends VehicleOrderInputModel {
  userConfirmation?: boolean
}
export interface ReturnFormData extends VehicleReturnInputModel {
  confirmedDeliveryDate?: string
}

export function CombinedScheduler({ dataRow, visible, onClose, getCostCenterName }: Props) {
  const { t } = useTranslation('common')
  const [deliveryFormData, setDeliveryFormData] = useState<DeliveryFormData>()
  const [returnFormData, setReturnFormData] = useState<ReturnFormData>({})
  const [returnsReadOnly, setReturnsReadOnly] = useState(true)
  const [canAutoFillReturns, setCanAutoFillReturns] = useState(false)
  const [returnConfirmation, setReturnConfirmation] = useState(false)

  const [updateVehicleOrder] = usePutVehicleOrderMutation()
  const [updateVehicleReturn, { isError }] = usePutVehicleReturnMutation()
  const [error, setError] = useState(false)

  const canSubmit = returnConfirmation && (deliveryFormData?.userConfirmation ?? false)
  const calculateReturnDate = (deliveryDate?: string) => {
    if (!deliveryDate) return undefined
    const date = new Date(deliveryDate)
    date.setDate(date.getDate() + 1 + (date.getDay() === 5 ? 2 : date.getDay() === 6 ? 1 : 0))
    return zeroTimeZoneDate(date)
  }
  const handleDeliveryDataUpdate = (data: DeliveryFormData) => {
    setDeliveryFormData(data)
    if (returnsReadOnly || !returnFormData.scheduledReturnDate) {
      const returnDate = calculateReturnDate(data.confirmedDelivery)

      const returnAutoFillData: ReturnFormData = {
        scheduledReturnAddress: data.confirmedDeliveryAddress,
        scheduledReturnContact: data.confirmedDeliveryContact,
        scheduledReturnDate: returnDate?.toISOString().slice(0, -5),
        confirmedDeliveryDate: returnDate?.toISOString().slice(0, -5),
      }
      setReturnFormData(returnAutoFillData)
      setCanAutoFillReturns(true)
    }
  }

  const returnsDataRow: ReturnsDataRow = {
    customer: dataRow.customer,
    contract: dataRow.replacedContract!,
    costCenter: dataRow.replacedContract ? getCostCenterName(dataRow.replacedContract) : '',
  }

  const handleDataSumbit = async () => {
    if (!canSubmit) return

    const id = dataRow.vehicleOrder?.id
    if (deliveryFormData && id) {
      try {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { userConfirmation, ...model } = deliveryFormData
        await updateVehicleOrder({ id, model }).unwrap()

        try {
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          const { confirmedDeliveryDate, ...returnModel } = returnFormData
          await updateVehicleReturn({ orderId: id, model: returnModel }).unwrap()
          onClose()
          setError(false)
        } catch (error) {
          setError(true)
          console.error('Error submitting data:', error)
        }
      } catch (error) {
        setError(true)
        console.error('Error submitting data:', error)
      }
    }
  }
  const confirmContainer = (
    <div className="scheduler-confirm-area">
      <div
        className="transparent-button negative"
        onClick={() => {
          onClose()
        }}
      >
        <i className="icon-close"></i>
        {t('CLOSE')}
      </div>
      {error && <div style={{ color: 'red' }}>{isError ? t('RETURN_UPDATE_ERROR') : t('DEFAULT_ERROR_MSG')}</div>}
      <div className={`transparent-button ${canSubmit ? 'positive' : 'disabled'}`} onClick={handleDataSumbit}>
        <i className="icon-check"></i>
        {t('CONFIRM')}
      </div>
    </div>
  )
  return (
    <Modal
      visible={visible}
      onClose={onClose}
      children={
        <div className="sheduler-content">
          <div className="combinedScheduler">
            <SchedulerModal
              dataRow={dataRow}
              visible={true}
              onClose={onClose}
              combinedView
              onDataUpdate={(data: DeliveryFormData) => handleDeliveryDataUpdate(data)}
            />
            <ReturnSchedulerModal
              visible={visible}
              onClose={() => {
                onClose()
              }}
              dataRow={returnsDataRow}
              combinedView
              autoFillInfo={returnFormData}
              canAutoFill={canAutoFillReturns}
              onAutoFillComplete={() => setCanAutoFillReturns(false)}
              onUserConfirmation={(val) => setReturnConfirmation(val)}
              onManualDataUpdate={(data) => setReturnFormData(data)}
              onReadOnlyToggle={(val) => setReturnsReadOnly(val)}
            />
          </div>
        </div>
      }
      footer={confirmContainer}
    ></Modal>
  )
}
